import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { PageHeader } from "@/components/layout/page-header";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LoadingState, ErrorState, EmptyState } from "@/components/ui/empty";
import { EntryTypeBadge } from "@/components/status-badges";
import { useApi } from "@/lib/api";
import { formatRelative, parseJson } from "@/lib/format";
import type { JournalEntryType, JournalResponse } from "@/lib/types";
import { cn } from "@/lib/utils";

type TypeFilter = JournalEntryType | "all";

export function JournalPage() {
  const journal = useApi<JournalResponse>("/api/journal");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [search, setSearch] = useState("");

  const counts = useMemo(() => {
    const m = new Map<JournalEntryType | "all", number>();
    const entries = journal.data?.entries ?? [];
    m.set("all", entries.length);
    for (const e of entries) {
      m.set(e.entry_type, (m.get(e.entry_type) ?? 0) + 1);
    }
    return m;
  }, [journal.data]);

  const filtered = useMemo(() => {
    const entries = journal.data?.entries ?? [];
    return entries.filter((e) => {
      if (typeFilter !== "all" && e.entry_type !== typeFilter) return false;
      if (
        search &&
        !`${e.title} ${e.body}`
          .toLowerCase()
          .includes(search.toLowerCase())
      ) {
        return false;
      }
      return true;
    });
  }, [journal.data, typeFilter, search]);

  if (journal.loading) return <LoadingState />;
  if (journal.error)
    return <ErrorState error={journal.error} onRetry={journal.refetch} />;

  const entryTypes: JournalEntryType[] = [
    "finding",
    "observation",
    "hypothesis",
    "methodology_note",
    "failure_diagnosis",
    "design_decision",
  ];

  return (
    <>
      <PageHeader
        title="Journal"
        description="Lab notebook. Observations, findings, hypotheses, methodology notes, and diagnoses."
      />

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <TypeChip
          label="All"
          count={counts.get("all") ?? 0}
          active={typeFilter === "all"}
          onClick={() => setTypeFilter("all")}
        />
        {entryTypes.map((t) => {
          const n = counts.get(t) ?? 0;
          if (n === 0) return null;
          return (
            <TypeChip
              key={t}
              label={t.replace("_", " ")}
              count={n}
              active={typeFilter === t}
              onClick={() => setTypeFilter(t)}
            />
          );
        })}
        <div className="ml-auto relative">
          <Search className="absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search titles + body…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 w-64 rounded-md border bg-background pl-7 pr-2 text-xs placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-ring"
          />
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState title="No entries" description="Try clearing filters." />
      ) : (
        <div className="relative space-y-3">
          {/* Vertical timeline spine */}
          <div className="absolute bottom-4 left-[7px] top-4 w-px bg-border" />
          {filtered.map((e) => {
            const tags = parseJson<string[]>(e.tags, []);
            return (
              <Link
                key={e.id}
                to={`/journal/${e.id}`}
                className="relative block pl-6 transition-transform hover:translate-x-0.5"
              >
                <div className="absolute left-[3px] top-4 h-2.5 w-2.5 rounded-full border-2 border-primary/60 bg-background" />
                <Card className="hover:border-muted-foreground/50">
                  <CardContent className="space-y-2 p-4">
                    <div className="flex items-center gap-2">
                      <EntryTypeBadge entryType={e.entry_type} />
                      <span className="text-[10px] text-muted-foreground">
                        {formatRelative(e.timestamp)}
                      </span>
                      <span className="text-[10px] text-muted-foreground">
                        · {e.author}
                      </span>
                    </div>
                    <h3 className="text-sm font-medium leading-snug">
                      {e.title}
                    </h3>
                    <p className="line-clamp-2 text-xs text-muted-foreground">
                      {stripMarkdown(e.body).slice(0, 220)}
                    </p>
                    {tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {tags.slice(0, 6).map((t) => (
                          <Badge
                            key={t}
                            variant="outline"
                            className="text-[9px] font-mono"
                          >
                            {t}
                          </Badge>
                        ))}
                        {tags.length > 6 && (
                          <span className="text-[9px] text-muted-foreground">
                            +{tags.length - 6}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-2 pt-1 text-[10px] text-muted-foreground">
                      {e.rung_id && (
                        <span className="font-mono">{e.rung_id}</span>
                      )}
                      {e.engine_version && (
                        <span className="font-mono">{e.engine_version}</span>
                      )}
                      {e.experiment_id && <span>· linked experiment</span>}
                      {e.campaign_id && <span>· linked campaign</span>}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </>
  );
}

function TypeChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex h-8 items-center gap-1.5 rounded-md border px-3 text-xs capitalize transition-colors hover:bg-accent",
        active && "bg-primary text-primary-foreground",
      )}
    >
      {label}
      <span className="text-[10px] opacity-70">{count}</span>
    </button>
  );
}

function stripMarkdown(s: string): string {
  return s
    .replace(/```[\s\S]*?```/g, "")
    .replace(/`[^`]+`/g, "")
    .replace(/[*_#>]/g, "")
    .replace(/\n+/g, " ")
    .trim();
}
